import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Clock, AlertTriangle, StickyNote, Link2 } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { formatCOP, statusLabel, statusColor } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';

const ES = {
  approved: 'Aprobado', requested: 'Solicitado', processing: 'En proceso', processed: 'Ejecutado', completed: 'Ejecutado',
  rejected: 'Rechazado', failed: 'Fallido', paid: 'Pagado', pending: 'Pendiente', declined: 'Rechazado', error: 'Error',
  APPROVED: 'Aprobado', DECLINED: 'Rechazado', VOIDED: 'Anulado', ERROR: 'Error', PENDING: 'Pendiente',
  order_cancelled: 'Pedido cancelado', awaiting_payment: 'Esperando pago', ready: 'Listo para desembolso',
  scheduled: 'Programado', refunded: 'Reembolsado', held: 'Retenido', delivered_to_buyer: 'Entregada al comprador',
  uploaded: 'Cargada', replaced: 'Reemplazada', wompi: 'Wompi', manual: 'Manual', simulated: 'Simulado',
};
const es = (v) => (v == null || v === '' ? null : ES[v] || v);
const SOURCE = { order: 'Pedido', order_events: 'Registro de eventos', wompi: 'Webhook Wompi', refund: 'Reembolso', pqr: 'PQR', invoice: 'Factura' };

const NR = <span className="text-slate-400 italic">No registrado</span>;
const dt = (v) => (v ? new Date(v).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : NR);

const Card = ({ title, children, testid, icon: Icon }) => (
  <section className="border border-slate-200 bg-white p-5" data-testid={testid}>
    <h2 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-orange-600" />}{title}
    </h2>
    <div className="mt-3 text-sm text-slate-700">{children}</div>
  </section>
);

const Field = ({ label, children, testid }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-slate-100 last:border-0" data-testid={testid}>
    <dt className="text-slate-500">{label}</dt><dd className="text-right font-medium text-slate-900 break-all">{children ?? NR}</dd>
  </div>
);

const Empty = ({ children }) => <p className="text-slate-400 italic">{children}</p>;

const General = ({ d }) => (
  <Card title="Información general" testid="order-detail-general">
    <dl>
      <Field label="Pedido"><span className="font-mono">{d.order.order_id}</span></Field>
      <Field label="Fecha de compra">{dt(d.order.created_at)}</Field>
      <Field label="Estado" testid="order-detail-status">
        <Badge className={`rounded-none border ${statusColor[d.order.status] || ''}`}>{statusLabel[d.order.status] || d.order.status}</Badge>
      </Field>
      <Field label="Método de pago">{es(d.order.payment_method)}</Field>
      <Field label="Comprador">{d.buyer.name}</Field>
      <Field label="Correo">{d.buyer.email}</Field>
      <Field label="Teléfono">{d.buyer.phone}</Field>
      <Field label="Dirección de envío">{[d.order.shipping.address, d.order.shipping.city].filter(Boolean).join(', ') || NR}</Field>
      <Field label="Vendedor(es)">{d.sellers.map((s) => s.name || s.user_id).join(', ') || NR}</Field>
      <Field label="Estado desembolso vendedor">{es(d.order.seller_payout_status)}</Field>
    </dl>
  </Card>
);

const Items = ({ d }) => (
  <Card title="Productos" testid="order-detail-items">
    <table className="w-full text-sm">
      <thead className="text-left text-[11px] uppercase text-slate-500"><tr><th>Producto</th><th>Cant.</th><th className="text-right">Precio</th></tr></thead>
      <tbody>{d.order.items.map((it, i) => (
        <tr key={`${it.product_id}-${i}`} className="border-t border-slate-100"><td className="py-1.5">{it.name}</td><td>{it.qty}</td><td className="text-right">{formatCOP(it.price)}</td></tr>
      ))}</tbody>
    </table>
    <dl className="mt-3">
      <Field label="Subtotal">{formatCOP(d.order.subtotal ?? d.order.total)}</Field>
      <Field label="Total pagado" testid="order-detail-total">{formatCOP(d.order.total)}</Field>
      <Field label="Comisión">{formatCOP(d.order.commission_amount)}</Field>
    </dl>
  </Card>
);

const Logistics = ({ d }) => {
  const shipped = d.timeline.find((t) => t.status === 'shipped' && t.source === 'order_events');
  return (
    <Card title="Seguimiento logístico" testid="order-detail-logistics">
      <dl>
        <Field label="Pago confirmado">{dt(d.order.paid_at)}</Field>
        <Field label="Preparación">{NR}</Field>
        <Field label="Despacho">{dt(shipped?.at)}</Field>
        <Field label="Transportadora">{NR}</Field>
        <Field label="Número de guía" testid="order-detail-tracking">{d.order.tracking_code}</Field>
        <Field label="Entrega">{dt(d.order.delivered_at)}</Field>
        <Field label="Confirmación de recibido">{dt(d.order.buyer_confirmed_at)}</Field>
        <Field label="Cancelación">{d.order.cancelled_at ? dt(d.order.cancelled_at) : '—'}</Field>
        <Field label="Incidencias / retrasos">{NR}</Field>
      </dl>
    </Card>
  );
};

const Payments = ({ d }) => (
  <Card title="Pagos" testid="order-detail-payments">
    {!d.payments.length && !d.wompi_events.length && <Empty>Sin registros de pago asociados.</Empty>}
    {d.payments.map((p) => (
      <dl key={p.payment_id} className="mb-3">
        <Field label="Pago">{p.payment_id}</Field>
        <Field label="Estado">{es(p.status)}</Field>
        <Field label="Referencia">{p.wompi_reference || p.reference}</Field>
        <Field label="Valor">{formatCOP(p.amount)}</Field>
        <Field label="Creado">{dt(p.created_at)}</Field>
      </dl>
    ))}
    {d.wompi_events.map((w, i) => (
      <div key={`${w.transaction_id}-${i}`} className="text-xs text-slate-600 border-t border-slate-100 pt-1">
        Wompi {es(w.status)} · tx {w.transaction_id || '—'} · {w.payment_method || '—'} · {dt(w.received_at)}
      </div>
    ))}
  </Card>
);

const Refunds = ({ d }) => (
  <Card title="Cancelaciones y reembolsos" testid="order-detail-refunds">
    {d.order.status === 'cancelled' && <p className="mb-2">Pedido cancelado el {dt(d.order.cancelled_at)}.</p>}
    {!d.refunds.length && <Empty>Sin reembolsos registrados.</Empty>}
    {d.refunds.map((r) => (
      <dl key={r.refund_id} className="mb-3" data-testid={`order-detail-refund-${r.refund_id}`}>
        <Field label="Reembolso">{r.refund_id}</Field>
        <Field label="Estado">{es(r.status)}</Field>
        <Field label="Valor">{formatCOP(r.amount)}</Field>
        <Field label="Motivo">{es(r.reason)}</Field>
        <Field label="Solicitado">{dt(r.requested_at)}</Field>
        <Field label="Ejecutado">{dt(r.processed_at)}</Field>
        <Field label="Referencia transacción">{r.gateway_ref}</Field>
        <Field label="Solicitado por">{r.requested_by_name || r.requested_by}</Field>
      </dl>
    ))}
    <p className="text-xs text-slate-400 mt-2">Solicitudes de devolución: flujo no disponible todavía. Notas crédito: no se registran por pedido.</p>
  </Card>
);

const Invoices = ({ d }) => (
  <Card title="Facturas del vendedor" testid="order-detail-invoices">
    {!d.invoices.length && <Empty>Sin facturas cargadas.</Empty>}
    {d.invoices.map((i) => (
      <dl key={i.invoice_id} className="mb-2">
        <Field label="Número">{i.invoice_number}</Field><Field label="Estado">{es(i.status)}</Field>
        <Field label="CUFE">{i.cufe}</Field><Field label="Cargada">{dt(i.uploaded_at || i.created_at)}</Field>
      </dl>
    ))}
  </Card>
);

const Pqrs = ({ d, orderId, reload }) => {
  const [ticket, setTicket] = useState('');
  const link = async () => {
    try {
      await api.post(`/admin/orders/${orderId}/link-pqr`, { ticket_number: ticket });
      toast.success('PQR vinculada'); setTicket(''); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo vincular'); }
  };
  return (
    <Card title="Quejas y reclamos (PQR)" testid="order-detail-pqrs" icon={Link2}>
      {!d.pqrs.length && <Empty>Sin PQR vinculadas a este pedido.</Empty>}
      {d.pqrs.map((p) => (
        <div key={p.pqr_id} className="mb-3 border-l-2 border-orange-300 pl-3" data-testid={`order-detail-pqr-${p.ticket_number}`}>
          <div className="font-semibold">{p.ticket_number} · {p.type} · {p.status}</div>
          <div className="text-xs text-slate-500">Abierta {dt(p.created_at)} · Responsable: {p.assignee || 'Sin asignar'}</div>
          <div className="mt-1"><strong>{p.subject}</strong>: {p.message}</div>
          <div className="mt-1 text-slate-600">Respuesta: {p.response || (p.replies?.length ? p.replies[p.replies.length - 1].text : 'Sin respuesta')}</div>
        </div>
      ))}
      <div className="mt-3 flex gap-2">
        <Input value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="PQR-AAAAMMDD-0001" className="rounded-none h-9"
               data-testid="order-detail-link-pqr-input" />
        <Button onClick={link} disabled={!ticket.trim()} className="rounded-none h-9" data-testid="order-detail-link-pqr-btn">Vincular</Button>
      </div>
      <p className="text-[11px] text-slate-400 mt-1">Solo se vincula si la PQR pertenece al comprador del pedido.</p>
    </Card>
  );
};

const Notes = ({ d, orderId, reload }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try { await api.post(`/admin/orders/${orderId}/notes`, { text }); setText(''); reload(); toast.success('Nota agregada'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Card title="Notas internas" testid="order-detail-notes" icon={StickyNote}>
      {!d.notes.length && <Empty>Sin notas.</Empty>}
      {d.notes.map((n) => (
        <div key={n.id} className="mb-2 bg-amber-50 border border-amber-100 p-2" data-testid={`order-detail-note-${n.id}`}>
          <div className="text-xs text-slate-500">{n.actor_name || n.actor_id} · {dt(n.at)}</div>
          <div className="whitespace-pre-wrap">{n.description}</div>
        </div>
      ))}
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="rounded-none mt-2" maxLength={2000}
                placeholder="Nota de seguimiento (no se puede editar después)" data-testid="order-detail-note-input" />
      <Button onClick={add} disabled={busy || text.trim().length < 3} className="rounded-none mt-2 bg-slate-900" data-testid="order-detail-note-submit-btn">
        Agregar nota
      </Button>
    </Card>
  );
};

const Timeline = ({ d }) => (
  <Card title="Historial cronológico" testid="order-detail-timeline" icon={Clock}>
    <ol className="relative border-l border-slate-200 ml-2">
      {d.timeline.map((t, i) => (
        <li key={`${t.at}-${i}`} className="mb-4 ml-4" data-testid={`order-timeline-item-${i}`}>
          <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white bg-orange-500" />
          <div className="text-xs text-slate-500">{dt(t.at)} · {SOURCE[t.source] || t.source}</div>
          <div className="font-semibold text-slate-900">{t.description}</div>
          <div className="text-xs text-slate-500">Responsable: {t.actor}</div>
        </li>
      ))}
    </ol>
    {!!d.missing.length && (
      <div className="mt-2 border-l-4 border-amber-400 bg-amber-50 p-3 text-xs text-amber-900" data-testid="order-detail-missing">
        <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />No registrado para este pedido: {d.missing.join(' · ')}.
      </div>
    )}
  </Card>
);

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  useSEO({ title: `Pedido ${orderId} | Admin` });
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => api.get(`/admin/orders/${orderId}/detail`)
    .then(({ data }) => setD(data)).catch((e) => setErr(e.response?.data?.detail || 'No se pudo cargar el pedido')), [orderId]);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-order-detail-page">
        <Link to="/admin/pedidos" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-orange-600" data-testid="order-detail-back-link">
          <ArrowLeft className="h-4 w-4" /> Pedidos
        </Link>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600 mt-3">Administración</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 break-all">Pedido {orderId}</h1>
        {err && <p className="mt-6 text-red-700" data-testid="order-detail-error">{err}</p>}
        {!d && !err && <p className="mt-6 text-slate-500">Cargando…</p>}
        {d && (
          <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_1fr]">
            <div className="space-y-5"><General d={d} /><Items d={d} /><Logistics d={d} /><Payments d={d} /></div>
            <div className="space-y-5"><Timeline d={d} /><Refunds d={d} /><Pqrs d={d} orderId={orderId} reload={load} />
              <Notes d={d} orderId={orderId} reload={load} /><Invoices d={d} /></div>
          </div>
        )}
      </div>
    </Layout>
  );
}
